import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IonButton, IonContent, IonPage, IonSpinner } from "@ionic/react";
import {
    FitnessDataType,
    GpsFilteredData,
    HalfEvent,
    HrData,
    MatchPhone,
} from "refsix-js-models";
import { getFitnessData } from "../../services/fitnessDataSync/fitnessDataSyncService";
import { useProfile, useUsername } from "../../components/hooks";
import { useTranslation } from "react-i18next";
import { calculateCompositeIntensity } from "../../utils/intensityCalculation";
import {
    saveMatchTimings,
    skipMatchTimings,
    sliderPositionsFromHalfEvents,
    wasCalibratedViaTimingsScreen,
} from "../../services/matchTimingsService";
import { trackEvent } from "../../services/analytics/analyticsService";
import MatchTimingsGraph from "./MatchTimingsGraph";
import HeaderComponent from "../../components/HeaderComponent";
import { AlertComponent } from "../../components";
import "./matchTimings.css";

interface MatchTimingsProps {
    match: MatchPhone;
    onComplete: () => void;
}

const DEFAULT_SLIDER_POSITIONS = [0.2, 0.4, 0.6, 0.8];
const ANIMATION_START_POSITIONS = [0.01, 0.02, 0.03, 0.04];
const ANIMATION_DURATION_MS = 1000;
const STAGGER_MS = 200;
const MAX_DISPLAY_DURATION_MS = 3 * 60 * 60 * 1000;

function easeOut(t: number): number {
    return 1 - (1 - t) * (1 - t);
}

const MatchTimings: FC<MatchTimingsProps> = ({ match, onComplete }) => {
    const { t } = useTranslation();
    const profile = useProfile();
    const username = useUsername();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [gpsData, setGpsData] = useState<GpsFilteredData | undefined>();
    const [hrData, setHrData] = useState<HrData | undefined>();
    const [sliderPositions, setSliderPositions] = useState<number[]>([
        ...ANIMATION_START_POSITIONS,
    ]);
    const [hasAdjusted, setHasAdjusted] = useState(false);
    const [showUnadjustedAlert, setShowUnadjustedAlert] = useState(false);
    // Ref for the synchronous drag guard, state to keep Save disabled until the
    // sliders have settled - until then they hold interpolated positions that
    // nobody chose.
    const [animating, setAnimating] = useState(true);
    const animatingRef = useRef(true);
    const hasAnimatedRef = useRef(false);

    // Whether we are re-doing a calibration rather than setting one up for the
    // first time. Changes what Skip means - see handleSkip.
    const isRecalibration = useMemo(
        () => wasCalibratedViaTimingsScreen(match),
        [match]
    );

    // Load raw fitness data directly (bypassing FitnessDataProvider)
    useEffect(() => {
        const loadData = async () => {
            if (!match._id || !username) return;

            setLoading(true);
            try {
                if (match.hasTracking) {
                    const rawGps = await getFitnessData(
                        match._id,
                        username,
                        FitnessDataType.GPS
                    );
                    setGpsData(rawGps as GpsFilteredData);
                }
            } catch (e) {
                console.log("Error loading GPS data:", e);
            }

            try {
                if (match.hasHeartRate) {
                    const rawHr = await getFitnessData(
                        match._id,
                        username,
                        FitnessDataType.HeartRate
                    );
                    setHrData(rawHr as HrData);
                }
            } catch (e) {
                console.log("Error loading HR data:", e);
            }

            setLoading(false);
        };

        loadData();
    }, [match._id, username, match.hasTracking, match.hasHeartRate]);

    // Determine activity time boundaries from raw GPS/HR data
    const { activityStartTime, activityEndTime } = useMemo(() => {
        let start = 0;
        let end = 0;

        if (gpsData?.geoPoints && gpsData.geoPoints.length > 0) {
            start = gpsData.geoPoints[0].time || 0;
            end = gpsData.geoPoints[gpsData.geoPoints.length - 1].time || 0;
        }

        if (hrData?.heartRateValues && hrData.heartRateValues.length > 0) {
            const hrStart = hrData.heartRateValues[0].time || 0;
            const hrEnd =
                hrData.heartRateValues[hrData.heartRateValues.length - 1]
                    .time || 0;
            if (start === 0 || hrStart < start) start = hrStart;
            if (hrEnd > end) end = hrEnd;
        }

        if (end - start > MAX_DISPLAY_DURATION_MS) {
            end = start + MAX_DISPLAY_DURATION_MS;
        }

        return { activityStartTime: start, activityEndTime: end };
    }, [gpsData, hrData]);

    // Where the sliders should come to rest. On a re-calibration that is where
    // the user last left them, so they can see and correct what was saved.
    //
    // Recomputed on every store update, because match is a new object each
    // time - so never make an effect depend on this. Doing so restarts the
    // effect mid-flight; see the entrance animation below.
    const savedPositions = useMemo(
        () =>
            sliderPositionsFromHalfEvents(
                match,
                activityStartTime,
                activityEndTime
            ),
        [match, activityStartTime, activityEndTime]
    );
    const targetPositions = savedPositions || DEFAULT_SLIDER_POSITIONS;

    // Read through a ref so the animation effect below never re-runs. The match
    // prop gets a new identity whenever the store updates, which on a
    // re-calibration gives savedPositions - and so targetPositions - a new
    // identity too.
    const targetPositionsRef = useRef(targetPositions);
    targetPositionsRef.current = targetPositions;

    // Mirrors the committed positions so a drag can clamp against them without
    // the state updater having to decide whether anything moved.
    const sliderPositionsRef = useRef(sliderPositions);
    sliderPositionsRef.current = sliderPositions;

    // Animate sliders from the top down to their resting positions on mount.
    // Runs exactly once: re-running would snap the sliders back out from under
    // a user who has already started dragging them, and cancelling a run in
    // flight would strand them at the top with animatingRef stuck true.
    useEffect(() => {
        if (loading) return;

        if (hasAnimatedRef.current) {
            // A reload (the fitness flags can flip while stats recalculate)
            // cancelled the run in flight. Settle the sliders where they were
            // heading instead of leaving them at an interpolated frame with
            // dragging and saving locked out. A completed animation is left
            // alone so it cannot undo the user's own dragging.
            if (animatingRef.current) {
                // Copied: the target is either the savedPositions memo or the
                // DEFAULT_SLIDER_POSITIONS module constant, and state must not
                // alias something a later drag could mutate.
                setSliderPositions([...targetPositionsRef.current]);
                animatingRef.current = false;
                setAnimating(false);
            }
            return;
        }
        hasAnimatedRef.current = true;

        const targets = targetPositionsRef.current;
        let startTime: number | null = null;
        let rafId: number;

        const totalDuration =
            ANIMATION_DURATION_MS + STAGGER_MS * (targets.length - 1);

        const animate = (timestamp: number) => {
            if (startTime === null) startTime = timestamp;
            const elapsed = timestamp - startTime;

            const interpolated = ANIMATION_START_POSITIONS.map((start, i) => {
                const sliderDuration = ANIMATION_DURATION_MS + i * STAGGER_MS;
                const t = Math.min(elapsed / sliderDuration, 1);
                const eased = easeOut(t);
                return start + (targets[i] - start) * eased;
            });

            setSliderPositions(interpolated);

            if (elapsed < totalDuration) {
                rafId = requestAnimationFrame(animate);
            } else {
                animatingRef.current = false;
                setAnimating(false);
            }
        };

        rafId = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(rafId);
    }, [loading]);

    // Calculate max HR from profile
    const maxHR = useMemo(() => {
        if (profile?.dob && match?.date) {
            const birthDate = new Date(profile.dob);
            const matchDate = new Date(match.date);
            const age = matchDate.getFullYear() - birthDate.getFullYear();
            return 220 - age;
        }
        return 190;
    }, [profile?.dob, match?.date]);

    // Calculate intensity data using synthetic timings
    const intensityData = useMemo(() => {
        if (!gpsData?.geoPoints || gpsData.geoPoints.length === 0) return [];

        // Create synthetic single-element timing to span the entire activity
        const syntheticTimings: HalfEvent[] = [
            {
                timestamp: activityStartTime,
                endTime: activityEndTime,
            } as HalfEvent,
        ];

        return calculateCompositeIntensity(
            gpsData,
            hrData,
            syntheticTimings,
            maxHR
        );
    }, [gpsData, hrData, activityStartTime, activityEndTime, maxHR]);

    // Constrained slider change handler (disabled during entrance animation)
    const handleSliderChange = useCallback(
        (index: number, newPosition: number) => {
            if (animatingRef.current) return;

            const prev = sliderPositionsRef.current;
            const minGap = 0.01;

            const lower = index === 0 ? 0 : prev[index - 1];
            const upper = index === prev.length - 1 ? 1 : prev[index + 1];
            const minVal = lower + minGap;
            const maxVal = upper - minGap;
            // Pre-loaded positions come from a previous save rather than from
            // dragging, so the neighbours can already sit closer than minGap.
            // Then no position keeps the gap on both sides - moving would snap
            // the slider onto a neighbour and save a zero length period.
            if (minVal > maxVal) return;

            const clamped = Math.max(minVal, Math.min(maxVal, newPosition));
            // The gesture fires on any touch, with no movement threshold, so
            // only a position that actually moved counts as an adjustment -
            // otherwise a stray tap silently disables the save confirmation.
            if (clamped === prev[index]) return;

            setHasAdjusted(true);
            setSliderPositions((current) => {
                const updated = [...current];
                updated[index] = clamped;
                return updated;
            });
        },
        []
    );

    const handleSkip = useCallback(async () => {
        if (!profile || saving) return;

        setSaving(true);
        try {
            // On a re-calibration this resets the match to a single period
            // rather than leaving the previously saved halves in place. Report
            // what it actually did rather than what was intended - the two
            // diverge when there is no activity range to reset to.
            const { reset } = await skipMatchTimings(
                match,
                profile,
                activityStartTime,
                activityEndTime
            );
            trackEvent(
                reset ? "PlayingPeriodsReset" : "PlayingPeriodsSkipped",
                {
                    matchId: match._id,
                    hasTracking: match.hasTracking,
                    hasHeartRate: match.hasHeartRate,
                }
            );
            onComplete();
        } catch (e) {
            console.error("Error skipping match timings:", e);
        } finally {
            setSaving(false);
        }
    }, [
        match,
        profile,
        saving,
        onComplete,
        activityStartTime,
        activityEndTime,
    ]);

    // Nothing to slice up if the fitness data failed to download - saving would
    // write zero length periods over the match. Skip stays available so an
    // auto-opened screen is still dismissable.
    const hasActivityRange = activityEndTime > activityStartTime;

    // Removing periods needs the activity range to collapse them back into, so
    // without it the button would claim to remove them and quietly not. A
    // first-run skip has nothing to undo and is always allowed - which also
    // keeps the auto-opened screen dismissable, as that only ever happens on a
    // match that has never been calibrated.
    const canRemovePeriods = !isRecalibration || hasActivityRange;

    const saveTimings = useCallback(async () => {
        if (!profile || saving || !hasActivityRange || animatingRef.current) {
            return;
        }

        setSaving(true);
        try {
            const periodLengths = await saveMatchTimings(
                match,
                sliderPositions,
                activityStartTime,
                activityEndTime,
                profile
            );
            trackEvent("PlayingPeriodsSaved", {
                matchId: match._id,
                hasTracking: match.hasTracking,
                hasHeartRate: match.hasHeartRate,
                wasRecalibration: isRecalibration,
                adjustedSliders: hasAdjusted,
                ...periodLengths,
            });
            onComplete();
        } catch (e) {
            console.error("Error saving match timings:", e);
        } finally {
            setSaving(false);
        }
    }, [
        match,
        sliderPositions,
        activityStartTime,
        activityEndTime,
        profile,
        saving,
        onComplete,
        isRecalibration,
        hasAdjusted,
        hasActivityRange,
    ]);

    // Saving without touching a slider stores an arbitrary even split of the
    // activity, which is almost never what the user meant - so check first.
    // Not needed when the sliders were pre-loaded with previously saved
    // periods: saving those again just re-writes what is already there.
    const handleSave = useCallback(() => {
        if (animatingRef.current) return;
        if (!hasAdjusted && !savedPositions) {
            setShowUnadjustedAlert(true);
            return;
        }
        saveTimings();
    }, [hasAdjusted, savedPositions, saveTimings]);

    if (loading) {
        return (
            <div className="match-timings-content">
                <div className="match-timings-loading">
                    <IonSpinner />
                </div>
            </div>
        );
    }

    return (
        <div className="match-timings-content">
            <p className="match-timings-description">
                {t("matchTimings.matchTimingsDescription")}
            </p>

            <MatchTimingsGraph
                intensityData={intensityData}
                sliderPositions={sliderPositions}
                onSliderChange={handleSliderChange}
                activityStartTime={activityStartTime}
                activityEndTime={activityEndTime}
            />

            <div className="match-timings-save-wrapper">
                <IonButton
                    className="match-timings-skip-btn"
                    data-testid="matchTimingsSkipBtn"
                    fill="outline"
                    onClick={handleSkip}
                    disabled={saving || !canRemovePeriods}
                >
                    {isRecalibration
                        ? t("matchTimings.removePeriods")
                        : t("general.skip")}
                </IonButton>
                <IonButton
                    className="match-timings-save-btn"
                    data-testid="matchTimingsSaveBtn"
                    onClick={handleSave}
                    disabled={saving || !hasActivityRange || animating}
                >
                    {saving ? (
                        <IonSpinner name="crescent" />
                    ) : (
                        t("general.save")
                    )}
                </IonButton>
            </div>

            <AlertComponent
                testId="match-timings-unadjusted-alert"
                showAlert={showUnadjustedAlert}
                title={t("matchTimings.confirmDefaultTitle")}
                description={t("matchTimings.confirmDefaultMessage")}
                dismissAlert={() => setShowUnadjustedAlert(false)}
                showAlternativeButton
                buttonAltText={t("general.cancel")}
                buttonDone={t("general.save")}
                onDonePressed={() => {
                    setShowUnadjustedAlert(false);
                    saveTimings();
                }}
            />
        </div>
    );
};

export default MatchTimings;
